// API helpers for the IPD Enhancements features (Cancel Admission, Sticker
// Print, Housekeeping). Tenant headers are sent explicitly on every call —
// the axios interceptor is unreliable in freshly-opened IPD tabs (per-tab
// sessionStorage), so we build them from sessionStorage here, matching the
// rest of the IPD data layer (see IpdDataContext.addReferredDoctor).

import axios from "axios";

const BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  "https://manage-dev.hlthtek.com/app/api/";

function authHeaders(extra = {}) {
  const token = sessionStorage.getItem("accessToken") || "";
  return {
    Authorization: `Bearer ${token}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
    ...extra,
  };
}

// ---- Cancel Admission ----------------------------------------------------

export async function getCancelReasons() {
  const res = await axios.get(`${BASE}ipd_admission_cancel_reasons/`, {
    headers: authHeaders(),
  });
  return res?.data?.data || [];
}

export async function cancelAdmission(admissionId, { reasonId, reason } = {}) {
  const body = {};
  if (reasonId) body.reason_id = reasonId;
  if (reason) body.reason = reason;
  const res = await axios.post(
    `${BASE}ipd_admission/${admissionId}/cancel/`,
    body,
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return res?.data;
}

// ---- Sticker -------------------------------------------------------------

export async function getSticker(admissionId) {
  const res = await axios.get(
    `${BASE}ipd_admission/${admissionId}/sticker/`,
    { headers: authHeaders() }
  );
  return res?.data?.data || null;
}

// ---- Housekeeping --------------------------------------------------------

export async function markBedCleaning(bedCode, { admissionId, notes } = {}) {
  const body = { bed_code: bedCode };
  if (admissionId) body.admission_id = admissionId;
  if (notes) body.notes = notes;
  const res = await axios.post(`${BASE}bed_housekeeping/`, body, {
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  return res?.data;
}

export async function completeBedCleaning(bedCode) {
  const res = await axios.put(
    `${BASE}bed_housekeeping/${encodeURIComponent(bedCode)}/complete/`,
    {},
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return res?.data;
}

// The housekeeping queue. Since Epic 5, marking a bed for cleaning sets
// `cleaning_state` and only moves Bed.status to "cleaning" when the bed has no
// active admission (see manage_housekeeping.py) — so an occupied bed flagged
// for cleaning never carries status="cleaning" and cannot be found by a
// ?status= query. ipd_bed/ has no cleaning_state filter, so filter client-side.
// `status === "cleaning"` is kept in the predicate to catch pre-Epic-5 rows,
// which were flagged via status alone and have a null cleaning_state.
export async function getBedsAwaitingCleaning() {
  const res = await axios.get(`${BASE}ipd_bed/`, { headers: authHeaders() });
  const beds = res?.data?.data || [];
  return beds.filter(
    (b) =>
      b.cleaning_state === "needs_cleaning" ||
      b.cleaning_state === "cleaning" ||
      String(b.status || "").toLowerCase() === "cleaning"
  );
}
